import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, FileSpreadsheet } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import EmptyState from "../../components/system/EmptyState";

/**
 * Phase 2E S7 — Catalog Import Run detail.
 *
 * Lives at ``/platform/companies/:cid/catalog/imports/:runId``.
 * Read-only — operators commit from the wizard, not from this page.
 */
function StatusBadge({ status }) {
  const color =
    status === "committed"
      ? "bg-emerald-100 text-emerald-900"
      : status === "review"
      ? "bg-amber-100 text-amber-900"
      : status === "cancelled"
      ? "bg-paper-sunk text-ink-2 line-through"
      : status === "failed"
      ? "bg-rose-100 text-rose-900"
      : "bg-paper-sunk text-ink-2";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-button font-mono text-[10px] uppercase tracking-wider ${color}`}
      data-testid={`run-status-${status}`}
    >
      {status || "—"}
    </span>
  );
}

function ClassificationBadge({ kind }) {
  const map = {
    add: "bg-emerald-100 text-emerald-900",
    update: "bg-amber-100 text-amber-900",
    unchanged: "bg-paper-sunk text-ink-2",
    needs_review: "bg-amber-100 text-amber-900",
    rejected: "bg-rose-100 text-rose-900",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-button font-mono text-[10px] uppercase tracking-wider ${
        map[kind] || "bg-paper-sunk text-ink-2"
      }`}
    >
      {kind || "—"}
    </span>
  );
}

function CountTile({ label, value, testid }) {
  return (
    <div className="bg-paper-2 rounded-card p-3" data-testid={testid}>
      <div className="data-sku mb-1">{label}</div>
      <div className="font-display font-black text-xl tabular-nums">
        {value ?? 0}
      </div>
    </div>
  );
}

export default function CatalogImportRunDetailPage() {
  const { cid, runId } = useParams();
  const nav = useNavigate();

  const [run, setRun] = useState(null);
  const [rows, setRows] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorText(null);
    try {
      const [coRes, runRes] = await Promise.all([
        api.get(`/platform/companies/${cid}`),
        api.get(`/platform/companies/${cid}/catalog/imports/${runId}`),
      ]);
      setCompany(coRes.data.company || coRes.data);
      setRun(runRes.data.run);
      setRows(runRes.data.rows || []);
    } catch (err) {
      const msg = formatApiErrorDetail(err);
      setErrorText(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [cid, runId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div data-testid="run-detail-loading" className="py-12 text-center">
        <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          Loading…
        </div>
      </div>
    );
  }
  if (errorText || !run) {
    return (
      <div data-testid="run-detail-error" className="py-12">
        <EmptyState
          icon={<FileSpreadsheet size={32} />}
          title="Run not found"
          description={errorText || "This import run does not exist or you cannot access it."}
          action={
            <Button
              tier="primary"
              onClick={() =>
                nav(`/platform/companies/${cid}/catalog/imports`)
              }
            >
              Back to imports
            </Button>
          }
        />
      </div>
    );
  }

  const c = run.row_counts || {};

  return (
    <div data-testid="run-detail-page">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}/catalog/imports`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="run-detail-back"
        >
          <ArrowLeft size={14} />
          Back to imports
        </Link>
      </div>

      <PageHeader
        eyebrow={
          <span className="font-mono text-[11px]">
            run_id: <span data-testid="run-detail-id">{run.id}</span>
          </span>
        }
        title="Import run"
        titleSuffix={<StatusBadge status={run.status} />}
        subtitle={
          <>
            Target tenant:{" "}
            <strong data-testid="run-detail-tenant-name">
              {company?.name || cid}
            </strong>
          </>
        }
        actions={
          <Button
            tier="secondary"
            onClick={load}
            data-testid="run-detail-refresh"
          >
            <RefreshCw size={14} /> Refresh
          </Button>
        }
      />

      <Card variant="raised" className="p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 font-mono text-[11px]">
          <div>
            <div className="data-sku mb-1">Source</div>
            <div>{run.source || "—"}</div>
          </div>
          <div>
            <div className="data-sku mb-1">Source label</div>
            <div>{run.source_label || "—"}</div>
          </div>
          <div>
            <div className="data-sku mb-1">Submitted</div>
            <div>{(run.submitted_at || "").slice(0, 19).replace("T", " ")}</div>
          </div>
          <div>
            <div className="data-sku mb-1">Committed</div>
            <div data-testid="run-detail-committed-at">
              {(run.committed_at || "—").slice(0, 19).replace("T", " ")}
            </div>
          </div>
          <div>
            <div className="data-sku mb-1">Cancelled</div>
            <div data-testid="run-detail-cancelled-at">
              {(run.cancelled_at || "—").slice(0, 19).replace("T", " ")}
            </div>
          </div>
          <div>
            <div className="data-sku mb-1">Submitted by</div>
            <div className="truncate">{run.submitted_by || "—"}</div>
          </div>
          <div>
            <div className="data-sku mb-1">Dry-run</div>
            <div>{run.dry_run ? "yes" : "no"}</div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <CountTile label="Staged" value={c.staged} testid="run-count-staged" />
        <CountTile label="Add" value={c.add} testid="run-count-add" />
        <CountTile label="Update" value={c.update} testid="run-count-update" />
        <CountTile
          label="Unchanged"
          value={c.unchanged}
          testid="run-count-unchanged"
        />
        <CountTile
          label="Needs review"
          value={c.needs_review}
          testid="run-count-needs-review"
        />
        <CountTile
          label="Rejected"
          value={c.rejected}
          testid="run-count-rejected"
        />
        <CountTile
          label="Committed"
          value={c.committed}
          testid="run-count-committed"
        />
      </div>

      <Card variant="raised" className="overflow-x-auto" data-testid="run-rows-card">
        <div className="min-w-[820px] grid grid-cols-[1fr_180px_180px_120px_120px_120px] gap-3 px-3 py-2 bg-paper-sunk border-b border-border/60 data-sku">
          <div>Product</div>
          <div>Barcode</div>
          <div>Department</div>
          <div>Classification</div>
          <div>Decision</div>
          <div>Committed PID</div>
        </div>
        {rows.slice(0, 500).map((r) => (
          <div
            key={r.id}
            className="min-w-[820px] grid grid-cols-[1fr_180px_180px_120px_120px_120px] gap-3 px-3 py-2 border-t border-border/30 text-sm"
            data-testid={`run-row-${r.id}`}
          >
            <div className="truncate" title={r.product_name}>
              {r.product_name || "—"}
            </div>
            <div className="font-mono text-xs truncate">{r.barcode || "—"}</div>
            <div className="font-mono text-xs truncate">
              {r.department_name_raw || "—"}
              {!r.department_id_resolved && (
                <span className="text-amber-700 ml-1">⚠</span>
              )}
            </div>
            <div>
              <ClassificationBadge kind={r.classification} />
            </div>
            <div className="font-mono text-[11px] uppercase">
              {r.review_decision}
            </div>
            <div className="font-mono text-[10px] truncate text-muted-foreground">
              {r.committed_product_id?.slice(0, 8) || "—"}
            </div>
          </div>
        ))}
        {rows.length > 500 && (
          <div className="text-center font-mono text-[11px] text-muted-foreground py-3">
            Showing first 500 of {rows.length} rows.
          </div>
        )}
      </Card>
    </div>
  );
}
